import { memo } from 'react';
import { Divider, HStack, VStack } from '@cbhq/cds-web/layout';
import {
  TextTitle3,
  TextHeadline,
  TextBody,
  TextLabel1,
  TextLabel2,
} from '@cbhq/cds-web/typography';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { ModalHeader } from '@cbhq/cds-web/overlays/modal/ModalHeader';
import { ModalBody } from '@cbhq/cds-web/overlays/modal/ModalBody';
import { Tag } from '@cbhq/cds-web/tag/Tag';
import { RemoteImage } from '@cbhq/cds-web/media';
import { DisplayType, format } from '@/utils/numberFormatter';
import { type UpcomingEvent } from './UpcomingEventsCard';

type Props = {
  event: UpcomingEvent | null;
  open: boolean;
  onClose: () => void;
};

// Metrics row component
const MetricRow = memo(function MetricRow({
  label,
  before,
  after,
}: {
  label: string;
  before: string;
  after: string;
}) {
  return (
    <HStack width="100%" alignItems="center" paddingY={1}>
      <TextBody as="span" style={{ flex: 1 }}>
        {label}
      </TextBody>
      <TextBody as="span" style={{ flex: 1, textAlign: 'right' }}>
        {before}
      </TextBody>
      <TextBody as="span" style={{ flex: 1, textAlign: 'right' }}>
        {after}
      </TextBody>
    </HStack>
  );
});

// Date row component
const DateRow = memo(function DateRow({ label, value }: { label: string; value: string }) {
  return (
    <HStack width="100%" justifyContent="space-between" alignItems="center" paddingY={0.5}>
      <TextHeadline as="span" color="fgMuted">
        {label}:
      </TextHeadline>
      <TextBody as="span" color="fgMuted">
        {value}
      </TextBody>
    </HStack>
  );
});

export const CorporateActionModal = memo(function CorporateActionModal({
  event,
  open,
  onClose,
}: Props) {
  if (!event) return null;

  // Mock data for metrics - in production this would come from the event or API
  const mockMetrics = {
    sharesBefore: 100,
    sharesAfter: 200,
    priceBefore: 150.0,
    priceAfter: 75.0,
    totalValueBefore: 15000.0,
    totalValueAfter: 15000.0,
  };

  // Determine if this is a stock split event
  const isStockSplit = event.eventLabel.toLowerCase().includes('split');

  return (
    <Modal visible={open} onRequestClose={onClose}>
      <ModalHeader title="Announcement" />
      <ModalBody>
        <VStack gap={3} paddingX={3} paddingY={2}>
          {/* Event header */}
          <HStack justifyContent="space-between" alignItems="flex-start" width="100%">
            <HStack alignItems="center" gap={1.5}>
              <RemoteImage
                source={event.tokenImageUrl}
                size="l"
                shape="circle"
                alt={event.tokenSymbol}
              />
              <VStack alignItems="flex-start" gap={0}>
                <TextHeadline as="span">{event.eventLabel}</TextHeadline>
                <TextLabel1 as="span" color="fgMuted">
                  {event.tokenSymbol}
                </TextLabel1>
              </VStack>
            </HStack>
            <VStack alignItems="flex-end" gap={0.5}>
              <Tag colorScheme="green">Upcoming</Tag>
              <TextLabel2 as="span" color="fgMuted">
                {event.detail}
              </TextLabel2>
            </VStack>
          </HStack>

          {/* Description */}
          <TextBody as="p" color="fgMuted">
            {isStockSplit
              ? `A ${event.detail} stock split will increase your share count while proportionally reducing the price per share. Your total investment value remains unchanged.`
              : `This corporate action for ${event.tokenName} (${event.tokenSymbol}) is scheduled for ${event.executionDate}. Please review the details below.`}
          </TextBody>

          {/* Metrics table - only show for stock splits */}
          {isStockSplit && (
            <VStack gap={1} width="100%" background="bgAlternate" borderRadius={500} padding={2}>
              {/* Header row */}
              <HStack width="100%" alignItems="center" paddingY={1}>
                <TextLabel1 as="span" color="fgMuted" style={{ flex: 1 }}>
                  Metric
                </TextLabel1>
                <TextLabel1 as="span" color="fgMuted" style={{ flex: 1, textAlign: 'right' }}>
                  Before Stock Split
                </TextLabel1>
                <TextLabel1 as="span" color="fgMuted" style={{ flex: 1, textAlign: 'right' }}>
                  After (Estimated)
                </TextLabel1>
              </HStack>

              <MetricRow
                label="Shares"
                before={format({ value: mockMetrics.sharesBefore, type: DisplayType.TokenAmount })}
                after={format({ value: mockMetrics.sharesAfter, type: DisplayType.TokenAmount })}
              />
              <MetricRow
                label="Price"
                before={format({ value: mockMetrics.priceBefore, type: DisplayType.Fiat })}
                after={format({ value: mockMetrics.priceAfter, type: DisplayType.Fiat })}
              />
              <MetricRow
                label="Total Value"
                before={format({ value: mockMetrics.totalValueBefore, type: DisplayType.Fiat })}
                after={format({ value: mockMetrics.totalValueAfter, type: DisplayType.Fiat })}
              />
            </VStack>
          )}

          {/* Divider */}
          <Divider direction="horizontal" />

          {/* Dates section */}
          <VStack gap={1.5} width="100%">
            <TextTitle3 as="h3">Dates</TextTitle3>
            <DateRow label="Execution Date" value={event.executionDate} />
          </VStack>
        </VStack>
      </ModalBody>
    </Modal>
  );
});
